import { myProactiveAgentNames } from '@/features/home/proactive-agent-visibility';
import { settingsStore } from '@/stores/settings.store';

import { useProactiveAgentSelection } from './proactive-agent-selection';
import {
  useIsChangingProactiveAgents,
  useProactiveAgentMutation,
  useProactiveAgentsQuery,
  type ProactiveAgentInstance,
  type ProactiveAgents,
} from './proactive-agent.query';

/** Whether an agent runs: the state of its chat, or why it has none */
export type ProactiveAgentActivity =
  | 'active'
  | 'paused'
  | 'stopped'
  | 'not-started'
  | 'cannot-start';

/**
 * One agent and its three states: whether it runs, whether it is the user's default on the server,
 * and whether this device's home screen shows its cards. Home follows the default unless this
 * device pins an agent, or no agent
 */
export type ProactiveAgentRow = {
  agent: string;
  chat: ProactiveAgentInstance | undefined;
  activity: ProactiveAgentActivity;
  isBuiltIn: boolean;
  canStart: boolean;
  // Whether the agent is one of the server's `default_agent_choices`. A default saved before the
  // server limited defaults to them can be another agent, which does not take the messages sent to
  // the proactive agent
  canBeDefault: boolean;
  isDefault: boolean;
  isSelected: boolean;
  isShownOnHome: boolean;
  // An agent that can be chosen as a default, is not the default, and is not stopped
  offersSetAsDefault: boolean;
  // While home filters cards by agent: an agent that can start or has a chat, and is not shown yet
  offersShowOnHome: boolean;
};

const activityOf = (
  chat: ProactiveAgentInstance | undefined,
  canStart: boolean
): ProactiveAgentActivity => {
  if (chat) return chat.state;
  return canStart ? 'not-started' : 'cannot-start';
};

/**
 * One row for each agent the server lists, including those that cannot start. `shownOnHome` is the
 * agent home shows, or 'none', and undefined while home does not filter cards by agent.
 */
const proactiveAgentRows = (
  agents: ProactiveAgents | undefined,
  homeOptions: readonly string[],
  shownOnHome: readonly string[] | undefined
): ProactiveAgentRow[] => {
  if (!agents) return [];
  const startable = new Set(agents.startable_agents);
  const builtIns = new Set(agents.builtin_agents);
  const defaultChoices = new Set(agents.default_agent_choices);
  return Object.keys(agents.configurations).map((agent) => {
    const chat = agents.instances.find((instance) => instance.agent === agent);
    const canStart = startable.has(agent);
    const activity = activityOf(chat, canStart);
    const isDefault = agent === agents.default_agent;
    const isShownOnHome = shownOnHome?.includes(agent) ?? false;
    const canBeDefault = defaultChoices.has(agent);
    return {
      agent,
      chat,
      activity,
      isBuiltIn: builtIns.has(agent),
      canStart,
      canBeDefault,
      isDefault,
      isSelected: myProactiveAgentNames(agents).includes(agent),
      isShownOnHome,
      offersSetAsDefault: canBeDefault && !isDefault && activity !== 'stopped',
      offersShowOnHome:
        shownOnHome !== undefined &&
        homeOptions.includes(agent) &&
        !isShownOnHome,
    };
  });
};

/** The user's proactive agents with their states, and the changes the list offers */
export const useProactiveAgentList = () => {
  // React Compiler would cache the settings store reads and miss their changes
  'use no memo';
  const agents = useProactiveAgentsQuery();
  const changeAgent = useProactiveAgentMutation();
  const isChanging = useIsChangingProactiveAgents();
  const selection = useProactiveAgentSelection(agents.data);
  const { names, selectedAgent, selectedAgents, followsDefault } = selection;
  const savedSelection = agents.data?.selection ?? {
    follow_default: true,
    agents: [],
  };
  // Until the agents load, the device's choice cannot be resolved, so the list says nothing about
  // home
  const describesHome =
    selection.homeFiltersByAgent && agents.data !== undefined;
  return {
    rows: proactiveAgentRows(
      agents.data,
      names,
      describesHome ? selectedAgents : undefined
    ),
    homeFollowsDefault: followsDefault,
    homeShowsNoAgent: describesHome && selectedAgent === 'none',
    offersShowNoAgentOnHome: describesHome && selectedAgent !== 'none',
    offersFollowDefaultOnHome: describesHome && !followsDefault,
    isChanging,
    canSelect: agents.data?.selection !== undefined,
    followsReleasedAgent: savedSelection.follow_default,
    setFollowsReleasedAgent: (follow_default: boolean) =>
      changeAgent.mutate({
        kind: 'select',
        selection: { ...savedSelection, follow_default },
      }),
    selectAgent: (agent: string, checked: boolean) =>
      changeAgent.mutate({
        kind: 'select',
        selection: {
          follow_default:
            savedSelection.follow_default &&
            !(agent === agents.data?.default_agent && !checked),
          agents: checked
            ? [...new Set([...savedSelection.agents, agent])]
            : savedSelection.agents.filter((name) => name !== agent),
        },
      }),
    // Refetches the list once the server answers, so the Default chip moves at once
    setAsDefault: (agent: string) =>
      changeAgent.mutate({ kind: 'set-default', agent }),
    // This device's home choice: pin an agent, pin no agent, or follow the default
    showOnHome: (agent: string) =>
      settingsStore.setSelectedProactiveAgent(agent),
    showNoAgentOnHome: () => settingsStore.setSelectedProactiveAgent('none'),
    followDefaultOnHome: () => settingsStore.setSelectedProactiveAgent(null),
  };
};
